import { Form, Input, Table } from '@bedrock/components';
import type {
  FormInstance,
  FormItemProps,
  FormProps,
} from '@bedrock/components/lib/Form';
import { ProTablePlus } from '@bedrock/eed-react';
import type { ColumnType, TableProps } from '@bedrock/components/lib/Table';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import classname from 'classnames/bind';
import styles from './index.less';
import React from 'react';
import SFormItem, { SFormItemProps } from '#components/SForm/SFormItem';
import { tableColumnWidthToRem } from '#utils';

const cx = classname.bind(styles);

// 定义表单列的类型
export interface FormColumn extends Omit<ColumnType, ''> {
  formItem?:
      | SFormItemProps
      | ((text: any, record: any, index: any) => SFormItemProps);
  required?: boolean;
}

// 定义列渲染参数的类型
interface columnRenderParams {
  add: (item?: any) => void;
  remove: (index: number | number[]) => void;
}

// 定义FormTable组件的属性类型
export interface FormTableProps<T>
    extends Omit<TableProps, 'data' | 'columns'> {
  value?: T[];
  onChange?: (value: T[]) => void;
  formColumns: FormColumn[] | ((p: columnRenderParams) => FormColumn[]);
  formProps?: Omit<FormProps, 'form' | 'initialValues'>;
}

// 定义FormTable组件的ref类型
export interface FormTableRef {
  add: (item?: any) => void;
  remove: (index: number | number[]) => void;
  form: FormInstance<Record<'list', any[]>>;
}

// 定义FormTable组件
const TableForm = forwardRef(function <T>(
    props: FormTableProps<T>,
    ref: React.Ref<FormTableRef>
) {
  // 使用useControllableValue自定义hook管理表单的值
  const [list, setList] = useControllableValue<T[]>(props, {});  
  // 使用Form.useForm创建表单实例
  const [form] = Form.useForm();
  const innerTableRef = useRef(null)

  // 从props中解构出formProps，并从formProps中解构出onValuesChange和restFormProps
  const {
    formProps = {},
    value,
    onChange,
    formColumns,
    ...otherTableProps
  } = props;  
  const { onValuesChange, ...restFormProps } = formProps;

  // 使用useEffect在list变化时更新表单的值
  useEffect(() => {
    if (!list) {
      form.setFieldsValue({ list: [] });
    } else {
      form.setFieldsValue({ list: [...list] });
    }
  }, [list]);

  // 定义添加表单项的方法
  const add = useMemoizedFn((item?: T | T[]) => {
    const newList = list?.length ? [...list] : [];
    if (item) {
      if (Array.isArray(item)) {
        newList.push(...item);
      } else {
        newList.push(item);
      }
    } else {
      // @ts-ignore
      newList.push({});
    }
    setList(newList);
  });

  // 定义删除表单项的方法
  const remove = useMemoizedFn((index: number | number[]) => {
    let newList = [];
    if (Array.isArray(index)) {
      newList = list.filter((n, i) => !index.includes(i));
    } else {
      newList = list.filter((n, i) => i !== index);
    }
    setList(newList);
  });

  // 使用useImperativeHandle自定义ref的暴露值
  useImperativeHandle(ref, () => ({
    add,
    remove,
    form,
    innerTableRef,
    validateFields: () => {
      return new Promise((resolve, reject) => {
        if (form?.validateFields) {
          form
              .validateFields()
              .then(() => {
                resolve(true);
              })
              .catch((error) => {
                reject(error);
              });
        } else {
          resolve(true);
        }
      });
    },
  }), [
    add,
    remove,
    form,
  ]);

  const renderTitle = (
      title: string | React.ReactNode,
      required: boolean,
      index: number
  ) => {
    return (
        <span key={index}>
        {required && <span className={cx('required-start')}>*</span>}
          {title}
      </span>
    );
  };

  // 使用useMemo计算表单列
  const columns = useMemo(() => {
    let columnsRes = formColumns;
    if (typeof columnsRes === 'function') {
      columnsRes = columnsRes({ add, remove });
    }
    return columnsRes?.map?.((column, index) => {
      const { formItem, required, ...otherColumn } = column;
      if (column?.children?.length) {
        return {
          ...tableColumnWidthToRem(otherColumn),
          title: renderTitle(column.title, column.required, index),
          className: cx('table-form-column', column.className),
          children: column.children.map((childColumn, childIndex) => {
            const { formItem: childFormItem } = childColumn;
            // console.log('childColumn', childColumn);
            const cItem=0;
            return {
              key: cItem,
              ...tableColumnWidthToRem(childColumn),
              title: renderTitle(
                  childColumn.title,
                  childColumn.required,
                  cItem
              ),
              className: cx('table-form-childColumn', childColumn.className),
              render: (text: any, record: any, index: any) => {
                if (!childFormItem) {
                  return childColumn.render
                      ? childColumn.render(text, record, index)
                      : '';
                }
                let formItemConfig =
                    typeof formItem === 'function'
                        ? formItem(text, record, index)
                        : formItem;
                let childFormItemConfig =
                    typeof childFormItem === 'function'
                        ? childFormItem(text, record, index)
                        : childFormItem;
                // console.log( 'childFormItemConfig',childColumn.dataIndex,);
                return (
                    <SFormItem
                        key={cItem}
                        label={childColumn.title}
                        showLabel={false}
                        required={childColumn.required}
                        {...childFormItemConfig}
                        className={cx(
                            { 'view-item': childFormItemConfig.view },
                            childFormItemConfig?.className
                        )}
                        formItemKey={[
                          index,
                          formItemConfig?.formItemKey || column.dataIndex,
                          cItem,
                          childFormItemConfig?.formItemKey || childColumn.dataIndex,
                        ]}
                        componentClass={
                            childFormItemConfig?.componentClass || 'input'
                        }
                    />
                );
              },
            };
          }),
        };
      }
      return {
        key: index,
        ...tableColumnWidthToRem(otherColumn),
        title: renderTitle(column.title, column.required, index),
        className: cx('table-form-column', column.className),
        render: (text: any, record: any, index: any) => {
          if (!formItem) {
            return column.render ? column.render(text, record, index) : text;
          }
          let formItemConfig =
              typeof formItem === 'function'
                  ? formItem(text, record, index)
                  : formItem;                  
          return (
              <SFormItem
                  key={index}
                  label={column.title}
                  showLabel={false}
                  required={column.required}
                  {...formItemConfig}
                  className={cx(
                      { 'view-item': formItemConfig.view },
                      formItemConfig?.className
                  )}
                  formItemKey={[
                    index,
                    formItemConfig?.formItemKey || column.dataIndex,
                  ]}
                  componentClass={formItemConfig?.componentClass || 'input'}
              />
          );
        },
      };
    });
  }, [props.formColumns]);


  // 返回FormTable组件的JSX
  return (
      <Form
          {...restFormProps}
          form={form}
          initialValues={{ list: list || [] }}
          onValuesChange={(changedValues, allValues) => {            
            setList([...allValues.list]);
            onValuesChange?.(changedValues, allValues);
          }}
      >
        <Form.List name="list">
          {(fields) => {
            return (
                <Table
                    ref={innerTableRef}
                    rowKey={(record, index) => index}
                    emptyText={<div style={{ textAlign: 'center', color: '#878A99' }}>暂无数据</div>}
                    {...otherTableProps}
                    className={cx('table-form', otherTableProps.className)}
                    wrapperClassName={cx('table-form-wrapper', otherTableProps.wrapperClassName)}
                    columns={columns}
                    data={list || []}
                />
            );
          }}
        </Form.List>
      </Form>
  );
});
export default TableForm;
